import {
  CheckIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  QrCodeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import walletService from '../../services/walletService';
import currencyService from '../../services/currencyService';

const DepositModal = ({ isOpen, onClose, allAssets = [] }) => {
  console.log(allAssets, "allAssets of deposit modal");

  // State variables
  const [selectedCoin, setSelectedCoin] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [depositAddress, setDepositAddress] = useState('');
  const [qrCodeVisible, setQrCodeVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userWallet, setUserWallet] = useState(null);
  
  // Dynamic data from backend
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [availableNetworks, setAvailableNetworks] = useState([]);
  const [feesAndLimits, setFeesAndLimits] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  // Load initial data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen]);

  // Handle MetaMask errors gracefully
  useEffect(() => {
    const handleMetaMaskError = (event) => {
      if (event.error && event.error.message && event.error.message.includes('MetaMask')) {
        console.warn('MetaMask error caught and ignored:', event.error.message);
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    // Listen for unhandled promise rejections (common for MetaMask errors)
    window.addEventListener('unhandledrejection', handleMetaMaskError);
    
    // Listen for global errors
    window.addEventListener('error', handleMetaMaskError);

    return () => {
      window.removeEventListener('unhandledrejection', handleMetaMaskError);
      window.removeEventListener('error', handleMetaMaskError);
    };
  }, []);

  // Load currencies and initial data
  const loadInitialData = async () => {
    try {
      setDataLoading(true);
      const currencies = await currencyService.getAvailableCurrencies();
      
      // Filter currencies that support deposits
      const depositCurrencies = [];
      for (const currency of currencies) {
        const supportsDeposits = await currencyService.supportsDeposits(currency.code);
        if (supportsDeposits) {
          depositCurrencies.push(currency);
        }
      }
      
      setAvailableCurrencies(depositCurrencies);
      
      // Set first currency as default if no coin selected
      if (depositCurrencies.length > 0 && !selectedCoin) {
        setSelectedCoin(depositCurrencies[0].code);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load currency data');
    } finally {
      setDataLoading(false);
    }
  };

  // Load networks when coin changes
  useEffect(() => {
    if (selectedCoin) {
      loadNetworksForCurrency(selectedCoin);
    }
  }, [selectedCoin]);

  // Load networks for selected currency
  const loadNetworksForCurrency = async (currencyCode) => {
    try {
      setIsLoading(true);
      const networks = await currencyService.getNetworksForCurrency(currencyCode);
      setAvailableNetworks(networks);
      
      // Set first network as default
      if (networks.length > 0) {
        setSelectedNetwork(networks[0].code);
      } else {
        setSelectedNetwork('');
      }
      
      // Reset wallet-related fields
      setDepositAddress('');
      setUserWallet(null);
      setFeesAndLimits(null);
    } catch (error) {
      console.error(`Error loading networks for ${currencyCode}:`, error);
      toast.error('Failed to load networks');
      setAvailableNetworks([]);
      setSelectedNetwork('');
    } finally {
      setIsLoading(false);
    }
  };

  // Load fees and limits when coin and network are selected
  useEffect(() => {
    // Only proceed if:
    // 1. Both coin and network are selected
    // 2. Networks are loaded (not empty and not loading)
    // 3. The selected network is valid for the selected currency
    const isNetworkValid = availableNetworks.length > 0 && 
                          availableNetworks.some(network => network.code === selectedNetwork);
    
    console.log('Deposit validation check:', {
      selectedCoin,
      selectedNetwork,
      availableNetworksCount: availableNetworks.length,
      isNetworkValid,
      isLoading,
      willProceed: selectedCoin && selectedNetwork && isNetworkValid && !isLoading
    });
    
    if (selectedCoin && selectedNetwork && isNetworkValid && !isLoading) {
      loadFeesAndLimits(selectedCoin, selectedNetwork);
      checkOrCreateWallet();
    }
  }, [selectedCoin, selectedNetwork, availableNetworks]);

  // Load fees and limits for currency/network
  const loadFeesAndLimits = async (currencyCode, networkCode) => {
    try {
      const feesData = await currencyService.getFeesAndLimits(currencyCode, networkCode);
      setFeesAndLimits(feesData);
      console.log('Fees and limits loaded:', feesData);
    } catch (error) {
      console.error(`Error loading fees and limits for ${currencyCode}/${networkCode}:`, error);
      setFeesAndLimits(null);
    }
  };

  // Get network-specific withdrawal fee
  const getNetworkWithdrawalFee = () => {
    if (!feesAndLimits || !selectedNetwork) return null;
    return currencyService.getNetworkWithdrawalFee(feesAndLimits, selectedNetwork, selectedCoin);
  };

  // Get deposit limits
  const getDepositLimits = () => {
    if (!feesAndLimits) return { min: '0', max: 'No limit' };
    return currencyService.getDepositLimits(feesAndLimits);
  };

  // Get withdrawal limits
  const getWithdrawalLimits = () => {
    if (!feesAndLimits) return { min: '0', max: 'No limit' };
    return currencyService.getWithdrawalLimits(feesAndLimits);
  };

  // Get confirmations for current network
  const getNetworkConfirmations = () => {
    if (!selectedNetwork) return 6;
    return currencyService.getNetworkConfirmations(selectedNetwork);
  };

  // Check if user has wallet or create new one
  const checkOrCreateWallet = async () => {
    try {
      setIsLoading(true);

      // Validate network is valid for the currency before making API calls
      const isNetworkValid = availableNetworks.some(network => network.code === selectedNetwork);
      if (!isNetworkValid) {
        console.warn(`Skipping wallet creation: Network ${selectedNetwork} not valid for ${selectedCoin}`);
        return;
      }

      // Step 1: Check if wallet exists using getwallets API
      const getWalletPayload = { currency: selectedCoin };
      
      // Only add blockchain_currency if it's different from the main currency (for tokens)
      if (selectedNetwork && selectedNetwork !== selectedCoin) {
        getWalletPayload.blockchain_currency = selectedNetwork;
      }
      
      let walletResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/getwallets/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(getWalletPayload)
      });

      if (walletResponse.ok) {
        const walletData = await walletResponse.json();
        console.log('Existing wallets:', walletData);
        
        // Check if we found a matching wallet
        const existingWallet = walletData.find(wallet => 
          wallet.currency === selectedCoin && 
          (selectedNetwork === selectedCoin || wallet.blockchain_currency === selectedNetwork)
        );
        
        if (existingWallet && !existingWallet.is_blocked) {
          // Use existing wallet
          setDepositAddress(existingWallet.address);
          setUserWallet(existingWallet);
          console.log('Using existing wallet:', existingWallet);
          return;
        }
      }

      // Step 2: Create new wallet if none exists or existing is blocked
      const createWalletPayload = { currency: selectedCoin };
      
      // Only add blockchain_currency if it's different from the main currency (for tokens)
      if (selectedNetwork && selectedNetwork !== selectedCoin) {
        createWalletPayload.blockchain_currency = selectedNetwork;
      }
      
      const createResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/create_new_wallet/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(createWalletPayload)
      });

      if (createResponse.ok) {
        const newWalletData = await createResponse.json();
        console.log('Created new wallet:', newWalletData);
        
        // Handle the response format (array of wallets)
        const newWallet = Array.isArray(newWalletData) ? newWalletData[0] : newWalletData;
        
        if (newWallet && newWallet.address) {
          setDepositAddress(newWallet.address);
          setUserWallet(newWallet);
          toast.success('New deposit address created!');
          
          if (newWallet.is_blocked) {
            toast.error('Deposits are currently blocked for this address');
          }
        } else {
          throw new Error('No wallet address returned from creation');
        }
      } else {
        let errorMessage = 'Failed to create wallet';
        try {
          const errorData = await createResponse.json();
          errorMessage = errorData.error || errorMessage;
          
          // If it's a network mismatch error, log it but don't show to user
          // (This should be prevented by validation, but handle gracefully if it occurs)
          if (errorMessage.includes('not supported for') || errorMessage.includes('Network')) {
            console.warn('Network mismatch error (should be prevented):', errorMessage);
            if (errorData.available_networks) {
              console.log('Available networks:', errorData.available_networks);
            }
            return; // Exit silently, don't show error to user
          }
        } catch (parseError) {
          // If response is not JSON (like HTML error page), show generic message
          console.error('Non-JSON error response:', parseError);
          if (createResponse.status === 500) {
            errorMessage = 'Server error occurred. Please check server logs.';
          }
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error getting deposit address:', error);
      // Don't show network mismatch errors to user
      if (!error.message || !error.message.includes('not supported for')) {
        toast.error(error.message || 'Failed to get deposit address');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle monitoring button click
  const handleStartMonitoring = async () => {
    if (!selectedCoin || !depositAddress) {
      toast.error('No deposit address available for monitoring');
      return;
    }

    try {
      setIsLoading(true);
      
      // Call monitoring API
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/monitor/deposits/?currency=${selectedCoin}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Monitoring started:', data);
        toast.success('Deposit monitoring is active');
      } else {
        throw new Error('Failed to start monitoring');
      }
    } catch (error) {
      console.error('Error starting monitoring:', error);
      toast.error('Failed to start monitoring');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Address copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy address');
    }
  };

  const getCurrentNetwork = () => {
    return availableNetworks.find(network => network.code === selectedNetwork);
  };

  // Format fee using currency service
  const formatFee = (fee) => {
    return currencyService.formatFee(fee);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal - Fixed height with scrollbar */}
        <div className="relative bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg mx-auto max-h-[90vh] flex flex-col">
          {/* Header - Fixed height */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700 flex-shrink-0">
            <h3 className="text-lg font-semibold text-white">
              Deposit {selectedCoin || 'Crypto'}
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-white transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content - Scrollable with fixed height */}
          <div className="p-6 space-y-6 overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            <div className="space-y-6">
              {/* Coin Selection */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Select Coin
                </label>
                <select
                  value={selectedCoin}
                  onChange={(e) => setSelectedCoin(e.target.value)}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  required
                  disabled={dataLoading}
                >
                  <option value="">{dataLoading ? 'Loading coins...' : 'Select a coin'}</option>
                  {availableCurrencies.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code}
                    </option>
                  ))}
                </select>
              </div>

              {/* Network Selection */}
              {selectedCoin && (
                <div>
                  <label className="block text-sm font-medium text-white mb-3">
                    Select Network
                  </label>
                  <select
                    value={selectedNetwork}
                    onChange={(e) => setSelectedNetwork(e.target.value)}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    required
                    disabled={isLoading}
                  >
                    <option value="">{isLoading ? 'Loading networks...' : 'Select a network'}</option>
                    {availableNetworks.map((network) => (
                      //if network is BitcoinV1 show BTC1 else show network.name
                      <option key={network.code} value={network.code}>
                        {network.name === 'BitcoinV1' ? 'BTC1' : network.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Deposit Instructions */}
              {selectedCoin && selectedNetwork && (
                <div className="bg-blue-900 bg-opacity-50 border border-blue-600 rounded-lg p-4">
                  <h4 className="text-blue-300 font-medium mb-2">How to Deposit {selectedCoin}</h4>
                  <ol className="text-sm text-blue-100 space-y-1">
                    <li>1. Copy your deposit address below</li>
                    <li>2. Send {selectedCoin} from your external wallet to this address</li>
                    <li>3. Wait for {getNetworkConfirmations()} confirmations</li>
                    <li>4. Your balance will be updated automatically</li>
                  </ol>
                </div>
              )}
            </div>

            {/* Deposit Address Section (if available) */}
            {depositAddress && (
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Your Deposit Address
                </label>
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-white">
                      {getCurrentNetwork()?.name === 'BitcoinV1' ? 'BTC1' : getCurrentNetwork()?.name} Network
                    </span>
                    <button
                      onClick={() => setQrCodeVisible(!qrCodeVisible)}
                      className="text-white hover:text-blue-300 text-xs transition-colors"
                    >
                      {qrCodeVisible ? 'Hide QR' : 'Show QR'}
                    </button>
                  </div>

                  <div className="break-all text-white text-sm mb-3 font-mono bg-gray-800 p-3 rounded">
                    {depositAddress}
                  </div>

                  <div className="space-y-3">
                    <button
                      onClick={() => copyToClipboard(depositAddress)}
                      className="w-full flex items-center justify-center py-2 px-4 bg-[#0E1696] !text-white hover:bg-blue-700 rounded-lg transition-colors"
                    >
                      {copied ? (
                        <CheckIcon className="h-4 w-4 mr-2" />
                      ) : (
                        <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
                      )}
                      {copied ? 'Copied!' : 'Copy Address'}
                    </button>
                    
                    <button
                      onClick={handleStartMonitoring}
                      disabled={isLoading}
                      className="w-full flex items-center justify-center py-2 px-4 bg-[#0E1696] !text-white  hover:bg-[#0E1696] disabled:bg-gray-600 rounded-lg transition-colors"
                    >
                      {isLoading ? 'Starting...' : 'Start Monitoring Deposits'}
                    </button>
                  </div>

                  {/* QR Code Placeholder */}
                  {qrCodeVisible && (
                    <div className="mt-4 flex justify-center">
                      <div className="w-48 h-48 bg-white rounded-lg flex items-center justify-center">
                        <QrCodeIcon className="h-32 w-32 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}



            {/* Network Details and Limits */}
            {getCurrentNetwork() && feesAndLimits && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-white mb-3">Deposit Limits & Network Info</h4>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-white">Network</div>
                        <div className="text-white font-medium">{getCurrentNetwork().name === 'BitcoinV1' ? 'BTC1' : getCurrentNetwork().name}</div>
                      </div>

                      <div>
                        <div className="text-white">Confirmations</div>
                        <div className="text-white font-medium">{getNetworkConfirmations()}</div>
                      </div>

                      <div>
                        <div className="text-white">Network Fee</div>
                        <div className="text-white font-medium">
                          {getNetworkWithdrawalFee() !== null ? `${formatFee(getNetworkWithdrawalFee())} ${selectedCoin}` : 'N/A'}
                        </div>
                      </div>
                      
                      <div>
                        <div className="text-white">Deposit Fee</div>
                        <div className="text-white font-medium">
                          {feesAndLimits?.limitsData?.fee?.deposit?.address 
                            ? `${formatFee(feesAndLimits.limitsData.fee.deposit.address)} ${selectedCoin}`
                            : 'Free'}
                        </div>
                      </div>
                      
                      <div>
                        <div className="text-white">Min Deposit</div>
                        <div className="text-white font-medium">{getDepositLimits().min} {selectedCoin}</div>
                      </div>
                      
                      <div>
                        <div className="text-white">Max Deposit</div>
                        <div className="text-white font-medium">{getDepositLimits().max} {selectedCoin}</div>
                      </div>
                      
                      <div>
                        <div className="text-white">Min Withdrawal</div>
                        <div className="text-white font-medium">{getWithdrawalLimits().min} {selectedCoin}</div>
                      </div>
                      
                      <div>
                        <div className="text-white">Max Withdrawal</div>
                        <div className="text-white font-medium">{getWithdrawalLimits().max} {selectedCoin}</div>
                      </div>
                    </div>
                    
                    {/* Additional Fee Information */}
                    {feesAndLimits?.limitsData?.fee && (
                      <div className="mt-4 pt-4 border-t border-gray-600">
                        <div className="text-xs text-white mb-2">Trading Fees</div>
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <span className="text-white">Limit Order: </span>
                            <span className="text-white">{(feesAndLimits.limitsData.fee.order?.limit * 100 || 0).toFixed(3)}%</span>
                          </div>
                          <div>
                            <span className="text-white">Market Order: </span>
                            <span className="text-white">{(feesAndLimits.limitsData.fee.order?.market * 100 || 0).toFixed(3)}%</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Loading indicator for fees */}
            {selectedCoin && selectedNetwork && !feesAndLimits && isLoading && (
              <div className="text-center text-white">
                Loading fees and limits...
              </div>
            )}
          </div>

          {/* Footer - Fixed height */}
          <div className="px-6 py-4 bg-gray-750 border-t border-gray-700 flex-shrink-0">
            <div className="flex justify-between items-center text-xs text-white">
              <span>Confirmations required: {getNetworkConfirmations()}</span>
              <span>Auto-monitoring active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepositModal; 